import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { Shelf, ShelfSkeleton } from '../components/Shelf';
import { TrackTable } from '../components/TrackTable';
import { Typewriter } from '../components/Typewriter';
import { Visualizer, type VisualizerStyle } from '../components/Visualizer';
import { usePlayer } from '../contexts/PlayerContext';
import { useShelves } from '../hooks/useShelves';
import { usePageMeta } from '../hooks/usePageMeta';
import { shelfSeeds } from '../data/seeds';
import { greeting } from '../utils/format';
interface HomeProps {
  visualizerStyle: VisualizerStyle;
  showHeroVisualizer: boolean;
}
export function Home({
  visualizerStyle,
  showHeroVisualizer
}: HomeProps) {
  const {
    shelves,
    isLoading,
    error
  } = useShelves();
  const {
    play,
    currentTrack,
    isPlaying,
    toggle
  } = usePlayer();
  const featured = shelves[0];
  const rotation = shelves.flatMap(shelf => shelf.tracks.slice(0, 3)).slice(0, 10);
  const heroTrack = currentTrack ?? featured?.tracks[0] ?? null;
  usePageMeta({
    title: 'Noctuzan — late-night radio, on demand',
    description: 'Neo-soul, lo-fi, jazz piano and synthwave pulled live from the iTunes catalogue. Real 30-second previews, an audio-reactive vinyl visualizer, local playlists, lyrics and an endless Night Drift queue.',
    image: currentTrack?.artwork,
    path: '/'
  });
  const startSet = () => {
    if (currentTrack) {
      toggle();
      return;
    }
    if (featured) play(featured.tracks, 0);
  };
  return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-282" data-mp="2883ys6gmghv4">
      <motion.header initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="flex flex-col gap-8 border-b border-ink-line pb-12 lg:flex-row lg:items-center lg:justify-between" data-id="element-283" data-mp="288vstcukm7ja">
        <div className="max-w-xl" data-id="element-284" data-mp="1u9nt8iuhwxek">
          <p className="text-sm text-muted" data-id="element-285" data-mp="1uafn9p8g2n2q">
            Tonight&apos;s transmission · {shelves.reduce((n, shelf) => n + shelf.tracks.length, 0)} previews loaded
          </p>
          <h1 className="mt-3 font-display text-5xl leading-[1.05] text-cream sm:text-6xl" data-id="element-286" data-mp="1onvml95blzyw">
            <Typewriter text={`${greeting()}.`} data-id="element-287" data-mp="1anukjqdbkmhu" />
            <span className="mt-1 block italic text-gold" data-id="element-288" data-mp="1fle14wxhpbcy">The needle&apos;s down.</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted" data-id="element-289" data-mp="23bgqv6dxm58e">
            {heroTrack ? `Spinning ${heroTrack.title} — ${heroTrack.artist}, off ${heroTrack.album}.` : 'Real records, pulled live from the iTunes catalogue. Thirty seconds each — just enough to know.'}
          </p>
          <div className="mt-7 flex items-center gap-4" data-id="element-290" data-mp="1yjyme8cp9mio">
            <button type="button" onClick={startSet} disabled={!featured && !currentTrack} className="flex items-center gap-2.5 rounded-full bg-gold px-6 py-3 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-291" data-mp="1yif7al2s5hih">
              {isPlaying ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-292" data-mp="kc0pp0rg1kz0" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-293" data-mp="3ss2ib5a2pxq7" />}
              {isPlaying ? 'Pause the set' : currentTrack ? 'Resume the set' : 'Start tonight’s set'}
            </button>
            {currentTrack ? <Link to="/now-playing" className="rounded-full border border-ink-line px-4 py-2.5 text-xs text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-294" data-mp="5ofm75qnpjqk">
                Lyrics &amp; live wave
              </Link> : <p className="max-w-[14rem] text-xs leading-relaxed text-muted" data-id="element-295" data-mp="27ey9hvtm7xy2">
                Previews are capped at ~30 seconds by Apple.
              </p>}
          </div>
        </div>

        {showHeroVisualizer && <div className="relative flex-none self-center" data-id="element-296" data-mp="2harkvbvllle9">
            <Visualizer style={visualizerStyle} className="h-64 w-64 sm:h-80 sm:w-80" data-id="element-297" data-mp="2mbfgkauruqet" />
            <p className="mt-2 text-center text-xs text-muted" data-id="element-298" data-mp="2qdafx9t56tpy">
              {isPlaying ? 'reacting to live audio' : 'paused — needle lifted'}
            </p>
          </div>}
      </motion.header>

      {error && <p role="status" className="mt-10 rounded-sm border border-ink-line bg-ink-panel px-4 py-3 text-sm text-cream" data-id="element-299" data-mp="303vckrp99o68">
          {error}
        </p>}

      <div className="mt-12" data-id="element-300" data-mp="3a104z99p2fvh">
        {isLoading ? shelfSeeds.slice(0, 2).map(seed => <ShelfSkeleton key={seed.id} title={seed.title} subtitle={seed.subtitle} data-id="element-301" data-mp="3e1k24cu9pjhm" />) : shelves.map(shelf => <Shelf key={shelf.id} shelf={shelf} data-id="element-302" data-mp="12g1f6tzuwdp9" />)}
      </div>

      {rotation.length > 0 && <section aria-labelledby="rotation" className="mt-16" data-id="element-303" data-mp="ycbpg17g3q2n">
          <h2 id="rotation" className="font-display text-2xl text-cream" data-id="element-304" data-mp="tea4faqglgoj">
            Tonight&apos;s rotation
          </h2>
          <p className="mt-0.5 text-sm text-muted" data-id="element-305" data-mp="og0d5b7uv087">Ten cuts, pulled across every row above.</p>
          <div className="mt-4 overflow-x-auto scroll-thin" data-id="element-306" data-mp="1m38r8l5j1706">
            <TrackTable tracks={rotation} data-id="element-307" data-mp="1m4s9ghaqts4l" />
          </div>
        </section>}
    </div>;
}